/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Maps the internal parsed model to the public c2pa-types `ManifestStore`.
 * Structural mapping only — signature_info and validation_* are supplied by the
 * verification steps via {@link MapOptions} (null/absent until then).
 */

import type { ClaimGeneratorInfo, Ingredient, Manifest, ManifestAssertion, ManifestStore, SignatureInfo, ValidationResults, ValidationState, ValidationStatus } from '../types'
import { toJsonValue } from '../util/toJsonValue'
import type { ParsedManifest, ParsedStore, ParsedAssertion } from '../model/parseManifestStore'
import type { HashedUriRef } from '../claim/parseClaim'
import { transformCawgIdentity } from '../cawg/transformIdentity'

export interface MapOptions {
    /** Per-manifest signature info (from COSE/X.509 parsing), keyed by manifest label. */
    signatureInfos?: Map<string, SignatureInfo>
    /** Store-level validation status list. */
    validationStatus?: ValidationStatus[]
    /** Store-level validation results. */
    validationResults?: ValidationResults
    /** Store-level three-state result. */
    validationState?: ValidationState | null
    /**
     * Manifest labels whose `cawg.identity` (X.509) COSE signature failed to
     * verify — the reported identity `signature_info` is withheld (CAWG Identity §8:
     * do not surface an unverified identity as validated).
     */
    cawgWithholdSignatureInfo?: Set<string>
}

function isIngredientLabel (label: string): boolean {
    return label.startsWith('c2pa.ingredient')
}

/**
 * Strips the deprecated `success` boolean from each `ValidationStatus` entry
 * surfaced from an ingredient's embedded validation data. c2pa-rs marks that
 * field `#[serde(skip_serializing)]` (a serialized status is `{code, url?,
 * explanation?}`), so the raw passthrough must drop it. The `success` *array*
 * inside a `StatusCodes` object is preserved (only objects bearing a `code` are
 * status entries).
 */
function stripDeprecatedStatusFields (v: unknown): unknown {
    if (Array.isArray(v)) return v.map(stripDeprecatedStatusFields)
    if (v && typeof v === 'object') {
        const o = v as Record<string, unknown>
        const hasCode = 'code' in o
        const out: Record<string, unknown> = {}
        for (const [k, val] of Object.entries(o)) {
            if (hasCode && k === 'success') continue
            out[k] = stripDeprecatedStatusFields(val)
        }
        return out
    }
    return v
}

/**
 * The label c2pa-rs reports an assertion under. It reads a v1 `c2pa.actions`
 * assertion into the current Actions struct and reports it as `c2pa.actions.v2`
 * (other versioned labels, e.g. `c2pa.hash.bmff.v2`, are reported as stored).
 */
function reportLabel (label: string): string {
    if (label === 'c2pa.actions') return 'c2pa.actions.v2'
    return label
}

/** Extracts a manifest URN from a JUMBF URI, e.g. `…/c2pa/urn:c2pa:abc[/…]` → `urn:c2pa:abc`. */
function manifestUrnFromUri (url: string | undefined): string | null {
    if (!url) return null
    const m = url.match(/\/c2pa\/([^/]+)/)
    return m ? m[1] : null
}

/** Splits a c2pa-rs `__N` duplicate-instance suffix off an assertion label. */
function splitInstance (label: string): { label: string, instance?: number } {
    const m = label.match(/^(.*)__(\d+)$/)
    return m ? { label: m[1], instance: Number(m[2]) } : { label }
}

/** The absolute JUMBF URI c2pa-rs reports for an assertion in a manifest. */
function assertionUri (manifestLabel: string, assertionLabel: string): string {
    return `self#jumbf=/c2pa/${manifestLabel}/c2pa.assertions/${assertionLabel}`
}

/** Infers an image MIME type from a thumbnail assertion label suffix. */
function inferThumbFormat (label: string): string {
    const ext = label.slice(label.lastIndexOf('.') + 1).toLowerCase()
    if (ext === 'jpeg' || ext === 'jpg') return 'image/jpeg'
    if (ext === 'png') return 'image/png'
    if (ext === 'webp') return 'image/webp'
    if (ext === 'gif') return 'image/gif'
    return 'application/octet-stream'
}

const THUMB_CLAIM = /^c2pa\.thumbnail\.claim/
const THUMB_INGREDIENT = /^c2pa\.thumbnail\.ingredient/

function toGeneratorInfoArray (raw: unknown): ClaimGeneratorInfo[] | undefined {
    if (raw === undefined || raw === null) return undefined
    const json = toJsonValue(raw)
    if (Array.isArray(json)) return json as ClaimGeneratorInfo[]
    return [json as ClaimGeneratorInfo]
}

function buildIngredient (a: ParsedAssertion, manifestLabel: string, byLabel: Map<string, ParsedAssertion>): Ingredient {
    const d = (a.decoded && typeof a.decoded === 'object' ? a.decoded : {}) as Record<string, unknown>
    const ing: Ingredient = { label: a.label }

    const title = d['dc:title']
    if (typeof title === 'string') ing.title = title
    const format = d['dc:format']
    if (typeof format === 'string') ing.format = format
    if (typeof d.instanceID === 'string') ing.instance_id = d.instanceID
    if (typeof d.relationship === 'string') ing.relationship = d.relationship as Ingredient['relationship']

    const activeUrl = (d.activeManifest as { url?: unknown })?.url ?? (d.c2pa_manifest as { url?: unknown })?.url
    const active = manifestUrnFromUri(typeof activeUrl === 'string' ? activeUrl : undefined)
    if (active) {
        ing.active_manifest = active
        ing.manifest_data = { format: 'application/c2pa', identifier: active }
    }
    if (d.validationResults) ing.validation_results = stripDeprecatedStatusFields(toJsonValue(d.validationResults)) as ValidationResults
    if (d.validationStatus) ing.validation_status = stripDeprecatedStatusFields(toJsonValue(d.validationStatus)) as ValidationStatus[]

    // Resolve the ingredient's thumbnail reference to a ResourceRef, taking the
    // MIME type from the referenced embedded-file assertion.
    const thumbUrl = (d.thumbnail as { url?: unknown })?.url
    if (typeof thumbUrl === 'string') {
        const refLabel = thumbUrl.slice(thumbUrl.lastIndexOf('/') + 1)
        const thumbAssertion = byLabel.get(refLabel)
        // The reference may already be absolute and point at another manifest's
        // thumbnail; preserve it. Otherwise resolve it against this manifest.
        const identifier = thumbUrl.startsWith('self#jumbf=/') ? thumbUrl : assertionUri(manifestLabel, refLabel)
        ing.thumbnail = {
            format: thumbAssertion?.mediaType || inferThumbFormat(refLabel),
            identifier
        }
    }
    return ing
}

function buildManifest (m: ParsedManifest, opts: MapOptions): Manifest {
    const manifest: Manifest = {
        label: m.label,
        claim_version: m.claimVersion
    }

    const genInfo = toGeneratorInfoArray(m.claim.claimGeneratorInfo)
    if (genInfo) manifest.claim_generator_info = genInfo
    if (m.claim.claimGenerator !== undefined) manifest.claim_generator = m.claim.claimGenerator
    if (m.claim.instanceId !== undefined) manifest.instance_id = m.claim.instanceId
    if (m.claim.title !== undefined) manifest.title = m.claim.title
    if (m.claim.format !== undefined) manifest.format = m.claim.format

    // Walk the claim's assertion references in order: created first, then gathered.
    // c2pa-rs lifts some assertions out of the reported `assertions` array:
    //   - c2pa.ingredient.*        → ingredients[]
    //   - c2pa.thumbnail.claim*    → manifest.thumbnail (ResourceRef)
    //   - c2pa.thumbnail.ingredient* → surfaced via the owning ingredient's thumbnail
    // Remaining assertions keep their (de-suffixed, version-normalized) label.
    const byLabel = new Map(m.assertions.map(a => [a.label, a]))
    const assertions: ManifestAssertion[] = []
    const ingredients: Ingredient[] = []
    const emit = (refs: HashedUriRef[], created: boolean): void => {
        for (const ref of refs) {
            const a = byLabel.get(ref.label)
            if (!a) continue
            if (isIngredientLabel(a.label)) {
                ingredients.push(buildIngredient(a, m.label, byLabel))
                continue
            }
            if (THUMB_CLAIM.test(a.label)) {
                manifest.thumbnail = {
                    format: a.mediaType || inferThumbFormat(a.label),
                    identifier: assertionUri(m.label, a.label)
                }
                continue
            }
            if (THUMB_INGREDIENT.test(a.label)) {
                continue // surfaced as ingredient.thumbnail
            }
            // c2pa-rs omits the plain `c2pa.hash.data` hard-binding from the
            // friendly manifest report (it keeps `.part` / `multi-asset` / `.bmff`).
            if (a.label === 'c2pa.hash.data') {
                continue
            }
            // c2pa-rs reports only modeled (CBOR/JSON) assertions in the manifest
            // JSON; raw `uuid`/binary assertions (e.g. `sony.original.signature`)
            // stay in the store for hashing but are not surfaced here.
            if (a.dataType !== 'cbor' && a.dataType !== 'json') {
                continue
            }
            const { label, instance } = splitInstance(reportLabel(a.label))
            // c2pa-rs transforms cawg.identity (embedded identity COSE → signature_info).
            // Withhold the identity's signature_info when its COSE signature failed
            // to verify (CAWG Identity §8) — the identity is not surfaced as validated.
            const data = a.label.startsWith('cawg.identity')
                ? (transformCawgIdentity(a.decoded, opts.cawgWithholdSignatureInfo?.has(m.label) ?? false) ?? toJsonValue(a.decoded))
                : toJsonValue(a.decoded)
            const entry: ManifestAssertion = { label, data }
            // c2pa-rs tags JSON-encoded assertions with kind:"Json"; CBOR is the
            // default and left implicit (ManifestAssertionKind).
            if (a.dataType === 'json') entry.kind = 'Json'
            if (created) entry.created = true
            if (instance !== undefined) entry.instance = instance
            assertions.push(entry)
        }
    }
    emit(m.claim.createdAssertions, m.claimVersion === 2)
    emit(m.claim.gatheredAssertions, false)

    if (ingredients.length > 0) manifest.ingredients = ingredients
    manifest.assertions = assertions

    if (m.claim.redactedAssertions.length > 0) manifest.redactions = m.claim.redactedAssertions

    const sig = opts.signatureInfos?.get(m.label)
    if (sig) manifest.signature_info = sig

    return manifest
}

/** Assembles the public ManifestStore from the parsed model. */
export function toManifestStore (parsed: ParsedStore, opts: MapOptions = {}): ManifestStore {
    const manifests: Record<string, Manifest> = {}
    for (const m of parsed.manifests) {
        manifests[m.label] = buildManifest(m, opts)
    }
    const store: ManifestStore = {
        active_manifest: parsed.activeLabel,
        manifests
    }
    if (opts.validationStatus) store.validation_status = opts.validationStatus
    if (opts.validationResults) store.validation_results = opts.validationResults
    if (opts.validationState !== undefined) store.validation_state = opts.validationState
    return store
}
